import React, { useEffect, useState } from "react";
import { Button, Header, Icon } from "semantic-ui-react";
import { useAgeReadyContext } from "../../AgeReadyContext/AgeReadyContext";
import { observer } from "mobx-react-lite";
import { SketchPicker } from 'react-color';
import { useParams } from "react-router";
import { IPartner } from "../../Stores/PartnerStore";
import { trackPromise, usePromiseTracker } from 'react-promise-tracker';

const AdminEditorComponent: React.FC = () => {
    const [showBackgroundColorPicker, setShowBackgroundColorPicker] = useState<boolean>(false);
    const [showNavigationBarColorPicker, setShowNavigationBarColorPicker] = useState<boolean>(false);
    const [showFontColorPicker, setShowFontColorPicker] = useState<boolean>(false);
    const [partner, setPartner] = useState<IPartner>();

    const LogoUploadElementId = 'logo-upload';

    const { caregiverStore, partnerStore } = useAgeReadyContext();
    const { partnerId } = useParams<{ partnerId: string }>();

    const { promiseInProgress } = usePromiseTracker();

    useEffect(() => {
        const existingPartner = partnerStore.partnersByIdMap.get(partnerId);

        if (existingPartner) {
            setPartner(existingPartner);
        }
    }, [partnerStore, partnerId]);

    if (!partner) {
        return null;
    }

    const onLogoSelectedAsync = async (event: any) => {
        const selectedFile = event.target.files[0];
        const encodedLogoString = await trackPromise(toBase64Async(selectedFile));
        if (!!encodedLogoString) {
            await trackPromise(partnerStore.setLogo((encodedLogoString as string), partnerId));
            partnerStore.loadLogo(partnerId);
        }
    }

    const toBase64Async = (file: File) => new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        fileReader.readAsDataURL(file);
        fileReader.onload = () => resolve(fileReader.result);
        fileReader.onerror = error => reject(error);
    });

    const launchFileChooser = () => {
        const uploadRef = document.getElementById(LogoUploadElementId);
        uploadRef?.click();
    }

    const onDeleteClicked = () => {
        partnerStore.deleteLogo(caregiverStore.partnerCode);
    }

    const toggleBackgroundColorPicker = () => {
        setShowBackgroundColorPicker(!showBackgroundColorPicker);
    }

    const onBackgroundColorSelected = (color: any) => {
        partner.setBackgroundColor(color.hex);
    };

    const toggleNavigationBarColorPicker = () => {
        setShowNavigationBarColorPicker(!showNavigationBarColorPicker);
    }

    const onNavigationBarColorSelected = (color: any) => {
        partner.setNavigationBarColor(color.hex);
    };

    const toggleFontColorPicker = () => {
        setShowFontColorPicker(!showFontColorPicker);
    }

    const onFontColorSelected = (color: any) => {
        partner.setFontColor(color.hex);
    };

    const updatePartner = async () => {
        if (!partner) {
            return;
        }

        await trackPromise(partnerStore.savePartner(partner));
        setShowNavigationBarColorPicker(false);
        setShowBackgroundColorPicker(false);
        setShowFontColorPicker(false);
    }

    return (
        <div style={{ minWidth: 500 }}>
            <div style={adminButtonStyles}>
                <input id={LogoUploadElementId} type="file" accept=".png,.jpg" onChange={onLogoSelectedAsync} hidden />
                <Button onClick={launchFileChooser}>Upload Logo</Button>
            </div>
            <div style={adminButtonStyles}>
                <Button onClick={onDeleteClicked}>Delete Logo</Button>
            </div>
            <div style={adminButtonStyles}>
                <Button onClick={toggleBackgroundColorPicker} icon floated="left"><Icon name="pencil" /></Button>
                <Header>
                    <Header.Content>
                        Background Color
                        <Header.Subheader>{partner.backgroundColor}</Header.Subheader>
                    </Header.Content>
                </Header>
                <div style={{ marginTop: '15px' }}>
                    {showBackgroundColorPicker && <SketchPicker
                        color={partner.backgroundColor}
                        onChangeComplete={onBackgroundColorSelected} />}
                </div>
            </div>
            <div style={adminButtonStyles}>
                <Button onClick={toggleNavigationBarColorPicker} icon floated="left"><Icon name="pencil" /></Button>
                <Header>
                    <Header.Content>
                        Navigation Bar Color
                        <Header.Subheader>{partner.navigationBarColor}</Header.Subheader>
                    </Header.Content>
                </Header>
                <div style={{ marginTop: '15px' }}>
                    {showNavigationBarColorPicker && <SketchPicker
                        color={partner.navigationBarColor}
                        onChangeComplete={onNavigationBarColorSelected} />}
                </div>
            </div>
            <div style={adminButtonStyles}>
                <Button onClick={toggleFontColorPicker} icon floated="left"><Icon name="pencil" /></Button>
                <Header>
                    <Header.Content>
                        Font Color
                        <Header.Subheader>{partner.fontColor}</Header.Subheader>
                    </Header.Content>
                </Header>
                <div style={{ marginTop: '15px' }}>
                    {showFontColorPicker && <SketchPicker
                        color={partner.fontColor}
                        onChangeComplete={onFontColorSelected} />}
                </div>
            </div>
            <div style={{ margin: 20, padding: 20, color: partner.fontColor, backgroundColor: partner.backgroundColor, borderRadius: 5 }}>
                This is a test to see if you like the color layout.
            </div>
            <div style={adminButtonStyles}>
                <Button onClick={updatePartner} primary loading={promiseInProgress}>Save</Button>
            </div>
        </div>
    );
};

const adminButtonStyles = {
    marginBottom: "60px",
}

export default observer(AdminEditorComponent);
